import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  Minus, 
  Send, 
  RotateCcw, 
  ChevronDown, 
  ChevronUp,
  Package,
  Loader2,
  Info,
  History,
  ShoppingCart,
  Eye,
  Download,
  CheckCircle,
  Clock
} from 'lucide-react';
import LogoLoader from '../components/LogoLoader';
import BottomNavigation from '../components/BottomNavigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RawMaterialOrder = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('order'); // 'order' or 'history'
  const [rawMaterials, setRawMaterials] = useState([]);
  const [previousOrders, setPreviousOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [productQuantities, setProductQuantities] = useState({});
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [additionalRequirements, setAdditionalRequirements] = useState('');
  
  // Order details modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (!user) {
      toast.error('Please login first');
      navigate('/');
      return;
    }
    fetchRawMaterials();
  }, [user, navigate]);

  useEffect(() => {
    if (activeTab === 'history' && user) {
      fetchPreviousOrders();
    }
  }, [activeTab, user]);

  const fetchRawMaterials = async () => {
    try {
      setLoading(true);
      const params = {
        franchise_id: user.franchise.id
      };
      
      const response = await axios.get(`${API}/raw-materials`, { params });
      setRawMaterials(response.data);
    } catch (error) {
      console.error('Error fetching raw materials:', error);
      toast.error('Failed to load raw materials');
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviousOrders = async () => {
    try {
      setLoadingOrders(true);
      const params = {
        franchise_id: user.franchise.id,
        limit: 50
      };
      
      const response = await axios.get(`${API}/raw-material-orders`, { params });
      setPreviousOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching previous orders:', error);
      toast.error('Failed to load previous orders');
    } finally {
      setLoadingOrders(false);
    }
  };

  const toggleCategory = (category) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const groupMaterialsByCategory = () => {
    const grouped = {};
    rawMaterials.forEach(material => {
      const category = material.category || 'Uncategorized';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(material);
    });
    return grouped;
  };

  const increaseQuantity = (materialId) => {
    setProductQuantities(prev => ({
      ...prev,
      [materialId]: (prev[materialId] || 0) + 1
    }));
  };

  const decreaseQuantity = (materialId) => {
    setProductQuantities(prev => {
      const currentQty = prev[materialId] || 0;
      if (currentQty <= 0) return prev;
      return {
        ...prev,
        [materialId]: currentQty - 1
      };
    });
  };

  const getQuantity = (materialId) => {
    return productQuantities[materialId] || 0;
  };

  const clearAll = () => {
    setProductQuantities({});
    setAdditionalRequirements('');
    toast.info('All quantities reset');
  };

  const calculateProductTotal = (material) => {
    const quantity = getQuantity(material.material_id);
    return quantity * (material.price || 0);
  };

  const calculateGrandTotal = () => {
    let total = 0;
    rawMaterials.forEach(material => {
      total += calculateProductTotal(material);
    });
    return total;
  };

  const downloadPDF = (base64Data, orderId) => {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Order_${orderId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadOrderPdf = async (orderId) => {
    try {
      setDownloadingPdf(true);
      const response = await axios.get(`${API}/raw-material-orders/${orderId}/pdf`);
      
      if (response.data.success && response.data.pdf_base64) {
        downloadPDF(response.data.pdf_base64, orderId);
        toast.success('PDF downloaded successfully');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setIsOrderModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login first');
      navigate('/');
      return;
    }
    
    // Build order items from quantities
    const orderItems = [];
    rawMaterials.forEach(material => {
      const quantity = getQuantity(material.material_id);
      if (quantity > 0) {
        orderItems.push({
          item_name: material.name,
          size_or_quantity: material.unit || 'unit',
          quantity: quantity,
          price_per_unit: material.price || 0,
          total_price: quantity * (material.price || 0)
        });
      }
    });
    
    if (orderItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const orderData = {
        franchise_id: user.franchise.id,
        franchise_name: user.franchise.name,
        location: user.franchise.location || user.franchise.city || '',
        contact_number: user.franchise.contact_number || '',
        ordered_items: orderItems,
        additional_requirements: additionalRequirements.trim()
      };
      
      const response = await axios.post(`${API}/raw-material-orders`, orderData);
      
      if (response.data.success) {
        // Download PDF
        if (response.data.pdf_base64) {
          downloadPDF(response.data.pdf_base64, response.data.order_id);
        }
        
        toast.success(`Order ${response.data.order_id} placed successfully!`, { duration: 5000 });
        
        // Clear form
        setProductQuantities({});
        setAdditionalRequirements('');
        
        // Switch to history tab to show the new order
        setActiveTab('history');
        fetchPreviousOrders();
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit order');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status, type) => {
    const configs = {
      payment: {
        'Pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
        'Paid': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
        'Partially Paid': { bg: 'bg-orange-100', text: 'text-orange-700', icon: Clock }
      },
      delivery: {
        'Pending': { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: Clock },
        'Delivered': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
        'In Transit': { bg: 'bg-blue-100', text: 'text-blue-700', icon: Package }
      }
    };
    
    const config = configs[type]?.[status] || { bg: 'bg-gray-100', text: 'text-gray-700', icon: Clock };
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-3 w-3" />
        {status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LogoLoader size="lg" text="Loading Raw Materials..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Full Screen Loader */}
      {submitting && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-6">
            <div className="relative w-32 h-32">
              <img 
                src="/logo.png" 
                alt="Logo" 
                className="w-24 h-24 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" 
              />
              <div className="w-32 h-32 border-4 border-t-white border-r-transparent border-b-transparent border-l-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-white text-xl font-semibold">Submitting your order...</p>
            <p className="text-white/70 text-sm">Please wait, do not close this page</p>
          </div>
        </div>
      )}

      {/* Header - with top padding for online status bar */}
      <div className="border-b border-border bg-surface shadow-sm pt-8">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <div className="flex items-center gap-3 flex-1">
            <Package className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-xl font-bold text-primary">Raw Material Order</h1>
              <p className="text-sm text-secondary">{user?.franchise?.name}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-4xl mx-auto px-4 pt-4">
        <div className="flex gap-2 p-1 bg-muted rounded-lg">
          <button
            onClick={() => setActiveTab('order')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md font-medium transition-colors ${
              activeTab === 'order' 
                ? 'bg-primary text-white shadow' 
                : 'text-secondary hover:bg-muted-foreground/10'
            }`}
          >
            <ShoppingCart className="h-4 w-4" />
            Place Order
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md font-medium transition-colors ${
              activeTab === 'history' 
                ? 'bg-primary text-white shadow' 
                : 'text-secondary hover:bg-muted-foreground/10'
            }`}
          >
            <History className="h-4 w-4" />
            My Orders
          </button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Place Order Tab */}
        {activeTab === 'order' && (
          <>
            {/* Franchise Info Card */}
            <Card className="mb-6 bg-primary text-white">
              <CardContent className="p-4">
                <p className="text-sm text-white/80 mb-1">Ordering as:</p>
                <p className="font-bold text-lg">{user?.franchise?.name}</p>
                <p className="text-sm text-white/90">{user?.franchise?.location || user?.franchise?.city}</p>
                <p className="text-sm text-white/80">{user?.franchise?.contact_number}</p>
              </CardContent>
            </Card>

            <form onSubmit={handleSubmit}>
              {/* Header with Clear Button */}
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold text-primary">Select Products</h2>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={clearAll}
                  className="rounded-full"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Clear All
                </Button>
              </div>

              {/* Products by Category */}
              <div className="space-y-4 mb-6">
                {Object.entries(groupMaterialsByCategory()).map(([category, materials]) => (
                  <Card key={category} className="overflow-hidden">
                    <div
                      className="flex justify-between items-center p-4 bg-primary text-white cursor-pointer"
                      onClick={() => toggleCategory(category)}
                    >
                      <h3 className="font-bold">{category}</h3>
                      {collapsedCategories[category] ? (
                        <ChevronDown className="h-5 w-5" />
                      ) : (
                        <ChevronUp className="h-5 w-5" />
                      )}
                    </div>

                    {!collapsedCategories[category] && (
                      <CardContent className="p-3 space-y-3">
                        {materials.map((material) => {
                          const quantity = getQuantity(material.material_id);
                          const total = calculateProductTotal(material);
                          
                          return (
                            <div
                              key={material.material_id}
                              className="flex flex-wrap justify-between items-center p-3 bg-muted rounded-xl gap-3"
                            >
                              <div className="flex-1 min-w-[150px]">
                                <p className="font-bold text-primary">{material.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  ₹{(material.price || 0).toFixed(2)} / {material.unit || 'unit'}
                                </p>
                                {material.description && (
                                  <p className="text-xs text-secondary mt-1 flex items-start gap-1">
                                    <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                    <span>{material.description}</span>
                                  </p>
                                )}
                              </div>
                              
                              <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2 bg-white rounded-lg p-1 shadow-sm">
                                  <Button
                                    type="button"
                                    size="icon"
                                    className="h-10 w-10 rounded-md bg-primary hover:bg-primary/90"
                                    onClick={() => decreaseQuantity(material.material_id)}
                                    disabled={quantity === 0}
                                  >
                                    <Minus className="h-4 w-4" />
                                  </Button>
                                  <span className="w-10 text-center font-bold text-primary">
                                    {quantity}
                                  </span>
                                  <Button
                                    type="button"
                                    size="icon"
                                    className="h-10 w-10 rounded-md bg-primary hover:bg-primary/90"
                                    onClick={() => increaseQuantity(material.material_id)}
                                  >
                                    <Plus className="h-4 w-4" />
                                  </Button>
                                </div>
                                
                                <div className="text-right min-w-[80px]">
                                  <p className="text-xs text-secondary">Total</p>
                                  <p className="font-bold text-primary">₹{total.toFixed(2)}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </CardContent>
                    )}
                  </Card>
                ))}

                {rawMaterials.length === 0 && (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                      <p className="text-muted-foreground font-medium">No raw materials available</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Contact admin to get raw materials assigned to your franchise
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Grand Total */}
              <Card className="mb-6 bg-primary text-white">
                <CardContent className="p-4 flex justify-between items-center">
                  <span className="text-lg font-semibold">Order Total:</span>
                  <span className="text-3xl font-bold">₹{calculateGrandTotal().toFixed(2)}</span>
                </CardContent>
              </Card>

              {/* Additional Requirements */}
              <Card className="mb-6 border-amber-200 bg-amber-50">
                <CardContent className="p-4">
                  <h3 className="text-base font-semibold text-primary mb-1">Additional Requirements (Optional)</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Request any items not listed above or special requirements
                  </p>
                  <Textarea
                    value={additionalRequirements}
                    onChange={(e) => setAdditionalRequirements(e.target.value)}
                    placeholder="E.g., Need extra packaging, specific delivery time, etc."
                    rows={3}
                    className="bg-white"
                  />
                </CardContent>
              </Card>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={submitting || calculateGrandTotal() === 0}
                className="w-full h-14 text-lg font-bold rounded-full bg-primary hover:bg-primary/90"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Submit Order
                  </>
                )}
              </Button>
            </form>
          </>
        )}

        {/* Order History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-primary">My Previous Orders</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchPreviousOrders}
                className="rounded-full"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>

            {loadingOrders ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                <p className="mt-2 text-secondary">Loading orders...</p>
              </div>
            ) : previousOrders.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <History className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground font-medium">No previous orders</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Your order history will appear here
                  </p>
                  <Button
                    className="mt-4"
                    onClick={() => setActiveTab('order')}
                  >
                    Place Your First Order
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {previousOrders.map((order) => (
                  <Card key={order.order_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-bold text-primary">{order.order_id}</p>
                            <p className="text-sm text-muted-foreground">{formatDate(order.timestamp)}</p>
                          </div>
                          <p className="font-bold text-lg text-primary">₹{(order.total_amount || 0).toLocaleString()}</p>
                        </div>
                        
                        <div className="flex gap-2">
                          {getStatusBadge(order.payment_status || 'Pending', 'payment')}
                          {getStatusBadge(order.delivery_status || 'Pending', 'delivery')}
                        </div>
                        
                        <p className="text-sm text-secondary">
                          {order.ordered_items?.length || 0} item(s)
                        </p>
                        
                        <div className="flex gap-2 pt-2 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleViewOrder(order)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleDownloadOrderPdf(order.order_id)}
                            disabled={downloadingPdf}
                          >
                            {downloadingPdf ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4 mr-2" />
                            )}
                            Download PDF
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      <Dialog open={isOrderModalOpen} onOpenChange={setIsOrderModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-primary">Order Details</DialogTitle>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-4">
              {/* Order Info */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-muted-foreground">Order ID</p>
                  <p className="font-bold text-primary text-sm">{selectedOrder.order_id}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Date & Time</p>
                  <p className="font-medium text-sm">{formatDate(selectedOrder.timestamp)}</p>
                </div>
              </div>

              {/* Status */}
              <div className="flex gap-2">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Payment</p>
                  {getStatusBadge(selectedOrder.payment_status || 'Pending', 'payment')}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Delivery</p>
                  {getStatusBadge(selectedOrder.delivery_status || 'Pending', 'delivery')}
                </div>
              </div>

              {/* Items */}
              <div>
                <h4 className="font-bold text-primary mb-2">Ordered Items</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-2 font-medium">Item</th>
                        <th className="text-right p-2 font-medium">Qty</th>
                        <th className="text-right p-2 font-medium">Price</th>
                        <th className="text-right p-2 font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {selectedOrder.ordered_items?.map((item, idx) => (
                        <tr key={idx}>
                          <td className="p-2">
                            <p className="font-medium">{item.item_name}</p>
                            <p className="text-xs text-muted-foreground">{item.size_or_quantity}</p>
                          </td>
                          <td className="p-2 text-right">{item.quantity}</td>
                          <td className="p-2 text-right">₹{(item.price_per_unit || 0).toFixed(2)}</td>
                          <td className="p-2 text-right font-medium">₹{(item.total_price || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-primary/5 font-bold">
                        <td colSpan={3} className="p-2 text-right">Grand Total:</td>
                        <td className="p-2 text-right text-primary">₹{(selectedOrder.total_amount || 0).toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Additional Requirements */}
              {selectedOrder.additional_requirements && (
                <div>
                  <h4 className="font-bold text-primary mb-2">Additional Requirements</h4>
                  <p className="text-sm text-secondary p-3 bg-amber-50 rounded-lg border border-amber-200">
                    {selectedOrder.additional_requirements}
                  </p>
                </div>
              )}

              {/* Download Button */}
              <Button
                className="w-full"
                onClick={() => handleDownloadOrderPdf(selectedOrder.order_id)}
                disabled={downloadingPdf}
              >
                {downloadingPdf ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Download Order PDF
                  </>
                )}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation */}
      <BottomNavigation 
        isOnline={true}
        activeTab="order"
      />
    </div>
  );
};

export default RawMaterialOrder;
